import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import {
    Form1099BoxPicker,
    Form1099BoxReadOnlyCell,
} from "@components/accounting/chartOfAccounts/Cells/Form1099BoxPicker";
import { isDraftAccountEligibleForForm1099Box } from "../../draftForm1099Box";
import { NestedEditorRow } from "../nestedRowTypes";
import { getNestedEditorTableMeta } from "../nestedTableMeta";

// Template-only 1099 box cell. Writes the draft (saved with the rest of the
// template), not the server. Ineligible account types show the stored label
// read-only, like the chart page.
export const NestedForm1099BoxCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, NestedEditorRow, unknown>) => {
        const { account } = row.original;
        const meta = getNestedEditorTableMeta(table);

        if (!account) {
            return <ViewTableCell />;
        }

        const currentBox = account.form1099Box ?? null;

        if (!isDraftAccountEligibleForForm1099Box(account)) {
            return <Form1099BoxReadOnlyCell value={currentBox} />;
        }

        const isLocked = meta?.isEditingDisabled === true;
        return (
            <ViewTableCell captureClicks={!isLocked}>
                <Form1099BoxPicker
                    value={currentBox}
                    isDisabled={isLocked}
                    onChange={(nextBox) => {
                        if (nextBox === currentBox) {
                            return;
                        }
                        meta?.callbacks?.onUpdateAccount(account.id, {
                            form1099Box: nextBox,
                        });
                    }}
                />
            </ViewTableCell>
        );
    },
);

NestedForm1099BoxCell.displayName = "NestedForm1099BoxCell";
